import { createContext, type ReactNode, useContext } from "react";
import { useCookieConsent } from "../hooks/useCookieConsent";
import type { CookieConsentResult } from "../types/cookieConsent";

interface CookieConsentContextValue {
	consent: CookieConsentResult | null;
	isLoading: boolean;
	error: Error | null;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(
	null,
);

interface CookieConsentProviderProps {
	children: ReactNode;
	policyUrl: string;
}

export function CookieConsentProvider({
	children,
	policyUrl,
}: CookieConsentProviderProps): React.ReactElement {
	const { consent, isLoading, error } = useCookieConsent({ policyUrl });

	return (
		<CookieConsentContext.Provider value={{ consent, isLoading, error }}>
			{children}
		</CookieConsentContext.Provider>
	);
}

/**
 * Returns cookie consent state. When used outside a CookieConsentProvider
 * (e.g. in the dev harness without app-prod), all consent is granted by default.
 */
export function useCookieConsentContext(): CookieConsentContextValue {
	const context = useContext(CookieConsentContext);
	if (!context) {
		return {
			consent: {
				consent: { "essential & security": true, analytics: true },
				isGranted: () => true,
			},
			isLoading: false,
			error: null,
		};
	}
	return context;
}
